import { Link } from 'react-router-dom'
import { PageHeader, StatTile, Panel, DataTable, Chip, BarChart, type Column } from '../ds/index'
import { LaunchChecklist } from '../components/LaunchChecklist'
import { useApi } from '../lib/useApi'
import { num, ago, dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { useSession } from '../lib/session'
import { EconomicsTiles, type Economics } from './Economics'
import { Funnel, HBars, RateBar, AttentionList } from '../components/Viz'

type Dash = {
  today: string; timezone: string
  counts: { emails: number; posts: number; leads: number; open_assignments: number; responded_today: number }
  week: { assignments: number; responded: number; skipped: number; median_response_s: number | null }
  reps: { id: string; name: string; open: number; responded_7d: number; assigned_7d: number; oldest_open: string | null; median_response_s: number | null }[]
  daily: { day: string; posts: number; leads: number; replies: number }[]
  funnel_7d: { posts: number; leads: number; sent: number; replied: number; sold: number }
  attention: { mailbox_problems: number; quiet_forwards: number; waiting_over_4h: number; held_posts: number; unset_groups: number; failed_posts: number; unrouted_leads_7d: number; digests_7d: number }
  by_group_7d: { name: string; posts: number; leads: number }[]
}

export const duration = (s: number | null | undefined) => s == null ? 'none yet' : s < 3600 ? `${Math.max(1, Math.round(s / 60))} min` : s < 172800 ? `${(s / 3600).toFixed(1)} hr` : `${Math.round(s / 86400)} days`

/** SPEC staff only. Never requested for an org admin, so the numbers never reach their browser. */
function SpecEconomics() {
  const { data } = useApi<Economics>('/api/spec/economics')
  if (!data) return null
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">SPEC economics <span className="muted" style={{ fontSize: 'var(--t-ui)', fontWeight: 400 }}>only SPEC sees this</span></h2>
        <Link to="/economics">Details and terms</Link>
      </div>
      {data.demo_data && <p className="post-cut" style={{ margin: '0 0 var(--s-4)' }}>Revenue is from invented demo subscribers.</p>}
      <EconomicsTiles e={data} compact />
    </Panel>
  )
}

export default function Dashboard() {
  const { me } = useSession()
  const { data, error } = useApi<Dash>('/api/dashboard')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const w = data.week
  const rate = w.assignments ? Math.round((w.responded / w.assignments) * 100) : null
  const a = data.attention

  const cols: Column<Dash['reps'][number]>[] = [
    { key: 'name', header: 'Rep', cell: (r) => <span className="avatar-cell"><span className="avatar avatar-sm" aria-hidden="true">{r.name.split(' ').map((x) => x[0]).join('').slice(0, 2)}</span><strong className="nowrap">{r.name}</strong></span>, sortBy: (r) => r.name },
    { key: 'open', header: 'Waiting', numeric: true, cell: (r) => r.open ? <Chip tone={r.oldest_open && Date.now() - Date.parse(r.oldest_open) > 4 * 3600e3 ? 'warn' : undefined}>{r.open}</Chip> : <span className="muted">0</span>, sortBy: (r) => r.open },
    { key: 'oldest', header: 'Oldest waiting', cell: (r) => r.oldest_open ? <span className="nowrap">{ago(r.oldest_open)}</span> : <span className="muted">none</span> },
    { key: 'rate', header: 'Replied, 7 days', cell: (r) => <RateBar num={r.responded_7d} den={r.assigned_7d} label={r.assigned_7d ? `${r.responded_7d} of ${r.assigned_7d}` : undefined} />, sortBy: (r) => r.assigned_7d ? r.responded_7d / r.assigned_7d : -1 },
    { key: 'speed', header: 'Typical reply', numeric: true, cell: (r) => <span className="nowrap">{duration(r.median_response_s)}</span>, sortBy: (r) => r.median_response_s ?? 1e12 },
  ]

  return (
    <>
      <PageHeader title="Dashboard" subtitle={`${dateShort(data.today)} in ${data.timezone.replace('_', ' ')}. Requests found in the groups, who has them, and how fast they get a reply.`} />
      <div className="launch-wrap"><LaunchChecklist /></div>
      {me?.user.role === 'spec' && <div style={{ marginBottom: 'var(--s-7)' }}><SpecEconomics /></div>}

      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Leads waiting on a rep" value={num(data.counts.open_assignments)} meta={<Link to="/leads">Open the board</Link>} />
        <StatTile label="Leads found today" value={num(data.counts.leads)} meta={`from ${num(data.counts.posts)} posts, ${num(data.counts.emails)} emails`} />
        <StatTile label="Replied, last 7 days" value={rate == null ? 'none' : `${rate}%`} meta={`${w.responded} of ${w.assignments} leads, ${w.skipped} skipped`} progress={rate ?? undefined} />
        <StatTile label="Typical time to reply" value={duration(w.median_response_s)} meta="median, last 7 days" />
      </div>

      <div className="wide-narrow">
        <Panel>
          <BarChart title="The last 14 days" subtitle="Unique posts, the leads among them, and replies made" height={260} showTable
                    data={data.daily.map((d) => ({ ...d, day: dateShort(d.day) }))} xKey="day"
                    series={[{ key: 'posts', label: 'Posts' }, { key: 'leads', label: 'Leads' }, { key: 'replies', label: 'Replies' }]} />
        </Panel>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Needs attention</h2></div>
          <AttentionList items={[
            { count: a.mailbox_problems, label: 'mailbox connection needs fixing, nothing is being read', to: '/mailbox', tone: 'neg' },
            { count: a.quiet_forwards, label: a.quiet_forwards === 1 ? 'rep with no alerts in 24 hours' : 'reps with no alerts in 24 hours', to: '/mailbox' },
            { count: a.waiting_over_4h, label: 'leads waiting over 4 hours', to: '/leads', tone: 'neg' },
            { count: a.unrouted_leads_7d, label: 'leads with nobody to send to', to: '/leads' },
            { count: a.held_posts, label: 'posts held in groups with no rep', to: '/groups' },
            { count: a.unset_groups, label: 'groups with no rep', to: '/groups' },
            { count: a.failed_posts, label: 'posts the AI could not screen', to: '/leads', tone: 'neg' },
            { count: a.digests_7d, label: 'digest emails this week, Facebook bundling posts', to: '/format-check' },
          ]} />
        </Panel>
      </div>

      <div className="two-col">
        <Panel>
          <Funnel title="From post to reply" subtitle="Last 7 days"
                  steps={[
                    { label: 'Posts seen', value: data.funnel_7d.posts, hint: 'each post counted once' },
                    { label: 'Requests for a service', value: data.funnel_7d.leads },
                    { label: 'Sent to a rep', value: data.funnel_7d.sent },
                    { label: 'Replied on Facebook', value: data.funnel_7d.replied },
                    { label: 'Sold to a buyer', value: data.funnel_7d.sold },
                  ]} />
          <p style={{ margin: 'var(--s-4) 0 0' }}><Link to="/activity">See every step on the activity timeline</Link> · <Link to="/categories">Leads by category</Link></p>
        </Panel>
        <Panel>
          <HBars title="Busiest groups" subtitle="Leads in the last 7 days" format={(n) => num(n)} empty="No posts this week."
                 rows={data.by_group_7d.map((g) => ({ label: g.name, value: g.leads, note: `${g.posts} posts` }))} />
        </Panel>
      </div>

      <Panel className="panel-flush">
        <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}><h2 className="panel-title">Reps</h2><Link to="/people">All people</Link></div>
        <DataTable columns={cols} rows={data.reps} rowKey={(r) => r.id} />
      </Panel>
    </>
  )
}
